import moment from 'moment';
import {
  getPricePerPaxTypePerCurrency,
  calculateTotalPricePerCurrency,
  findCurrencyItemFromListWithFallback,
} from '../../../util/currency';

export const StopoverTypes = {
  PRE_EVENT: 'PreEvent',
  POST_EVENT: 'PostEvent',
};

export const StopoverTypesLabel = {
  PreEvent: 'Pre-Event Stopover',
  PostEvent: 'Post-Event Stopover',
};

export function createSelectedStopover(mutableState, data) {
  if (!mutableState.stopovers) {
    // eslint-disable-next-line no-param-reassign
    mutableState.stopovers = [];
  }
  mutableState.stopovers.push(data);
}

export function getCheapestStopoverData(data, currencyPriceSetting) {
  if (!data?.length) return null;

  const sortedData = data
    .map((datum) => {
      const totalPricePerCurrency = calculateTotalPricePerCurrency(datum);
      console.debug('Stopover price calculation:', {
        datumKey: datum.key,
        totalPricePerCurrency,
      });
      return {
        ...datum,
        totalPricePerCurrency,
      };
    })
    .sort((a, b) => {
      const aPrice = findCurrencyItemFromListWithFallback(
        a.totalPricePerCurrency,
        currencyPriceSetting?.currency
      )?.price;
      const bPrice = findCurrencyItemFromListWithFallback(
        b.totalPricePerCurrency,
        currencyPriceSetting?.currency
      )?.price;
      return (aPrice || 0) - (bPrice || 0);
    });

  return sortedData[0];
}

export function getCheapestStopoverDataPrice(stopoverTourOptionDataArray, currencyPriceSetting) {
  return getCheapestStopoverData(stopoverTourOptionDataArray, currencyPriceSetting).price;
}

export function getMaxStopoverDataNumberOfNights(stopoverTourOptionDataArray) {
  let maxNumberOfNights = 0;

  if (stopoverTourOptionDataArray && stopoverTourOptionDataArray.length) {
    stopoverTourOptionDataArray.forEach(({ numberOfNights }) => {
      maxNumberOfNights = Math.max(maxNumberOfNights, numberOfNights);
    });
  }

  return maxNumberOfNights;
}

export function getMinStopoverDataNumberOfNights(stopoverTourOptionDataArray) {
  let minNumberOfNights = Infinity;

  if (stopoverTourOptionDataArray && stopoverTourOptionDataArray.length) {
    stopoverTourOptionDataArray.forEach(({ numberOfNights }) => {
      minNumberOfNights = Math.min(minNumberOfNights, numberOfNights);
    });
  } else {
    minNumberOfNights = 0;
  }

  return minNumberOfNights;
}

export function getMutableSelectedStopover(state, stopoverGroupKey) {
  return state.stopovers.find((s) => s?.groupKey === stopoverGroupKey);
}

export function getStopoverDataSoldOut(stopoverTourOptionDataArray) {
  return stopoverTourOptionDataArray.every((d) => d.soldOut);
}

export function getStopoverDataWithDataKey(stopoverOptions, dataKey) {
  let match;
  stopoverOptions.forEach((group) => {
    group.options.forEach((stopover) => {
      const dataMatch = stopover.data.find(({ key }) => key === dataKey);
      if (dataMatch) {
        match = dataMatch;
      }
    });
  });

  return match;
}

export function getStopoverPriceWithDataKey(stopoverOptions, dataKey) {
  const { price = 0 } = getStopoverDataWithDataKey(stopoverOptions, dataKey) || {};
  return price;
}

export function getStopoverGroupWithSelectedStopover(stopoverGroups, selectedStopover) {
  return stopoverGroups?.find((stopoverGroup) => stopoverGroup?.key === selectedStopover?.groupKey);
}

export function getStopoverOptionWithSelectedStopover(stopoverGroups, selectedStopover) {
  const foundStopoverGroup = getStopoverGroupWithSelectedStopover(stopoverGroups, selectedStopover);
  return foundStopoverGroup?.options.find(
    (stopoverOption) => stopoverOption?.key === selectedStopover.optionKey
  );
}

export function getSelectedStopoverOriginallyIncluded(selectedOptions, selectedStopover) {
  return selectedOptions.stopovers?.find(
    (stopover) =>
      stopover.optionKey === selectedStopover?.optionKey &&
      stopover.groupKey === selectedStopover.groupKey
  );
}

/**
 * Takes props from tour and calculates updated tourDates based on selectedStopovers
 * @param {object} props
 * @param {object} selectedStopovers
 * @returns string in date format
 */
export function getTourDatesWithStopoversConsidered(
  { stopoverOptions, tourFromDate, tourToDate },
  selectedStopovers
) {
  let updatedTourFromDate = tourFromDate;
  const preEventSelectedStopover = selectedStopovers.find((selectedStopover) => {
    const { stopoverType } =
      getStopoverGroupWithSelectedStopover(stopoverOptions, selectedStopover) || {};
    return stopoverType === StopoverTypes.PRE_EVENT;
  });

  if (preEventSelectedStopover) {
    const { numberOfNights = 0 } = getStopoverDataWithDataKey(
      stopoverOptions,
      preEventSelectedStopover.dataKey
    );

    updatedTourFromDate = moment(tourFromDate).add(-numberOfNights, 'd').toISOString();
  }

  let updatedTourToDate = tourToDate;
  const postEventSelectedStopover = selectedStopovers.find((selectedStopover) => {
    const { stopoverType } =
      getStopoverGroupWithSelectedStopover(stopoverOptions, selectedStopover) || {};
    return stopoverType === StopoverTypes.POST_EVENT;
  });

  if (postEventSelectedStopover) {
    const { numberOfNights = 0 } = getStopoverDataWithDataKey(
      stopoverOptions,
      postEventSelectedStopover.dataKey
    );
    updatedTourToDate = moment(tourToDate).add(numberOfNights, 'd').toISOString();
  }

  return { updatedTourFromDate, updatedTourToDate };
}

export const isStopoverOfType = (stopover, type) => {
  let stopoverType = stopover.stopoverType;

  if (typeof stopoverType === 'number') {
    stopoverType = Object.values(StopoverTypes)[stopoverType];
  }

  return stopoverType === type;
};

export function segregateStopoversByType(stopoverOptions) {
  return {
    preEventStopovers: stopoverOptions?.filter((stopover) =>
      isStopoverOfType(stopover, StopoverTypes.PRE_EVENT)
    ),
    postEventStopovers: stopoverOptions?.filter((stopover) =>
      isStopoverOfType(stopover, StopoverTypes.POST_EVENT)
    ),
  };
}
